import api, { getResponseError } from '../../services/api'
import {
  ICalculateInstallment,
  IOrder,
  IOrderCharge,
  TCheckoutOrderReport,
} from '../types/Order'
import { IErrorResponse, IResponse } from '../types/ErrorResponse'
import { TSale } from '../types/Sale'
import { numberOnly } from '../../masks/masks'
import { TCheckoutAbandonedPagination } from '../types/CheckoutAbandoned'
import {
  ITrackingFilter,
  TCheckoutAccessPagination,
  TCheckoutOrderAnticipationPagination,
  TCheckoutOrderDetails,
  TCheckoutOrderEmail,
  TCheckoutOrderPagination,
  TSubscriptionDetails,
} from '../types/CheckoutOrder'
import { ICheckoutOrderAnticipationsProp } from '../hooks/useCheckoutOrderAnticipations'
import { TPaymentMethodEnum } from '../types/Product'
import { TBalanceCurrency } from '../types/Tenant'
import RequestCache from '../../services/RequestCache'

interface IPaginationProp extends ITrackingFilter {
  page: number
  rows: number
  search?: string
  type: string
  report: TCheckoutOrderReport
  startDate?: Date | null | undefined
  endDate?: Date | null | undefined
  sellerType?: number | null
  products?: string[] | null
  affiliateds?: string[] | null
  payments?: string[] | null
  status?: string[] | null
  isAssurance?: boolean
  currency?: TBalanceCurrency
}

interface IAccessPaginationProp {
  page: number
  rows: number
  search?: string
  report: 'all' | 'order' | 'subscription'
  startDate?: Date | null | undefined
  endDate?: Date | null | undefined
  sellerType?: number | null
  products?: string[] | null
  affiliateds?: string[] | null
  payments?: string[] | null
  status?: string[] | null
  currency?: TBalanceCurrency
}

interface IPaginationDaysProp {
  page: number
  rows: number
  search?: string
  lastDays: number
  report?: 'order' | 'subscription'
}

const create = async ({ data }: { data: TSale }): Promise<IResponse> => {
  try {
    const { data: response } = await api.post('/Order', setSendData(data))
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
        data: response,
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error: any) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const setSendData = (data: TSale) => {
  const item: IOrder = {
    productPriceId: data.productPriceId,
    name: data.name,
    document: numberOnly(data.document),
    email: data.email,
    phone: numberOnly(data.phone),
    address: {
      city: data.address.city,
      complement: data.address.complement,
      neighborhood: data.address.neighborhood,
      number: data.address.number,
      reference: data.address.reference,
      state: data.address.state,
      street: data.address.street,
      zipCode: numberOnly(data.address.zipCode),
    },
    paymentData: {
      paymentMethod: data.payment.methodType,
    },
  }

  if (data.payment.cardValidate && data.payment.cardValidate !== null) {
    if (data.payment.cardValidate.includes('/')) {
      const cardValidate = data.payment.cardValidate.split('/')
      data.payment.cardExpiresMonth = Number(cardValidate[0])
      data.payment.cardExpiresYear = Number(cardValidate[1])
    }
  }

  if (item.paymentData.paymentMethod === TPaymentMethodEnum.CreditCard) {
    item.paymentData.creditCardData = {
      number: numberOnly(data.payment.cardNumber),
      name: data.payment.cardName,
      cvv: data.payment.cardCCV,
      year: data.payment.cardExpiresYear,
      month: data.payment.cardExpiresMonth,
      installments: data.payment.installment,
    }
  }

  if (item.paymentData.paymentMethod === TPaymentMethodEnum.DebitCard) {
    item.paymentData.debitCardData = {
      number: numberOnly(data.payment.cardNumber),
      name: data.payment.cardName,
      cvv: data.payment.cardCCV,
      year: data.payment.cardExpiresYear,
      month: data.payment.cardExpiresMonth,
    }
  }

  if (data?.products) {
    item.orderBumps = data.products.map((x) => ({
      productPriceId: x.productPriceId,
      description: x.productName,
      amount: x.amount,
      value: x.value,
    }))
  }

  return item
}

const verified = async ({
  chargeId,
}: {
  chargeId?: string
}): Promise<IOrderCharge | undefined> => {
  if (!chargeId) {
    return undefined
  }
  try {
    const { data: response } = await api.get(`/Order/charge/${chargeId}`)
    if (response) {
      return response
    }

    return undefined
  } catch (error: any) {
    const e = getResponseError(error)
    return undefined
  }
}

const calculeIsntallments = async ({
  priceId,
  newValue,
}: {
  priceId: string
  newValue: number
}): Promise<ICalculateInstallment | IErrorResponse> => {
  try {
    const { data: response } = await api.get(
      `/Order/price/calculate/${priceId}`,
      { params: { NewValue: newValue } }
    )
    if (response) {
      return response
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error: any) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getOpen = async ({
  page,
  rows,
  search,
  lastDays,
  report,
}: IPaginationDaysProp): Promise<
  TCheckoutAbandonedPagination | IErrorResponse
> => {
  try {
    var path = `/Order/open?PageNumber=${page}&RowsPerPage=${rows}&LastDays=${lastDays}`

    if (report) {
      path += `&Report=${report}`
    }

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    const { data } = await api.get<TCheckoutAbandonedPagination>(path)
    //const { data } = abandoneds;
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getAll = async ({
  page,
  rows,
  search,
  type,
  report,
  startDate,
  endDate,
  affiliateds,
  payments,
  products,
  sellerType,
  status,
  src,
  sck,
  utm_campaign,
  utm_content,
  utm_medium,
  utm_source,
  utm_term,
  isAssurance,
  currency,
}: IPaginationProp): Promise<TCheckoutOrderPagination | IErrorResponse> => {
  try {
    var path = `/Order/all?PageNumber=${page}&RowsPerPage=${rows}&Type=${type}`

    if (report !== 'all') {
      path += `&Report=${report}`
    }

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    if (startDate) {
      endDate ??= new Date()
      path += `&StartPeriod=${startDate.toISOString()}&EndPeriod=${endDate.toISOString()}`
    }

    if (affiliateds && affiliateds.length > 0) {
      path += `&Affiliateds=${affiliateds.join(';')}`
    }

    if (payments && payments.length > 0) {
      path += `&Payments=${payments.join(';')}`
    }

    if (products && products.length > 0) {
      path += `&Products=${products.join(';')}`
    }

    if (sellerType) {
      path += `&SellerType=${sellerType}`
    }

    if (status && status.length > 0) {
      path += `&Status=${status.join(';')}`
    }

    if (src) path += `&Src=${src}`
    if (sck) path += `&Sck=${sck}`
    if (utm_campaign) path += `&UtmCampaign=${utm_campaign}`
    if (utm_content) path += `&UtmContent=${utm_content}`
    if (utm_medium) path += `&UtmMedium=${utm_medium}`
    if (utm_source) path += `&UtmSource=${utm_source}`
    if (utm_term) path += `&UtmTerm=${utm_term}`

    if (isAssurance) path += `&IsAssurance=${isAssurance}`

    if (currency) path += `&Currency=${currency}`

    const { data } = await api.get<TCheckoutOrderPagination>(path)
    //const { data } = checkoutOrders;
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getApproved = async ({
  page,
  rows,
  search,
  type,
  report,
  startDate,
  endDate,
  affiliateds,
  payments,
  products,
  sellerType,
  status,
  src,
  sck,
  utm_campaign,
  utm_content,
  utm_medium,
  utm_source,
  utm_term,
  isAssurance,
  currency,
}: IPaginationProp): Promise<TCheckoutOrderPagination | IErrorResponse> => {
  try {
    const cache = new RequestCache(2)
    const cacheKey = 'OrderController.getApproved'
    var path = `/Order/approved?PageNumber=${page}&RowsPerPage=${rows}&Type=${type}`

    if (report) path += `&Report=${report}`

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    if (startDate) {
      endDate ??= new Date()
      path += `&StartPeriod=${startDate.toISOString()}&EndPeriod=${endDate.toISOString()}`
    }

    if (affiliateds && affiliateds.length > 0) {
      path += `&Affiliateds=${affiliateds.join(';')}`
    }

    if (payments && payments.length > 0) {
      path += `&Payments=${payments.join(';')}`
    }

    if (products && products.length > 0) {
      path += `&Products=${products.join(';')}`
    }

    if (sellerType) path += `&SellerType=${sellerType}`

    if (status && status.length > 0) {
      path += `&Status=${status.join(';')}`
    }

    if (src) path += `&Src=${src}`
    if (sck) path += `&Sck=${sck}`
    if (utm_campaign) path += `&UtmCampaign=${utm_campaign}`
    if (utm_content) path += `&UtmContent=${utm_content}`
    if (utm_medium) path += `&UtmMedium=${utm_medium}`
    if (utm_source) path += `&UtmSource=${utm_source}`
    if (utm_term) path += `&UtmTerm=${utm_term}`

    if (isAssurance) path += `&IsAssurance=${isAssurance}`

    if (currency) path += `&Currency=${currency}`

    const cachedData = cache.get<TCheckoutOrderPagination | IErrorResponse>(
      cacheKey,
      path
    )
    if (cachedData) return cachedData

    const { data } = await api.get<TCheckoutOrderPagination | null>(path)
    cache.set(cacheKey, path, data)
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getCheckoutOrderDetails = async ({
  id,
  productPriceId,
  orderChargeId,
}: {
  id: string
  productPriceId?: string
  orderChargeId?: string
}): Promise<TCheckoutOrderDetails | IErrorResponse> => {
  try {
    var path = `/Order/${id}/details`

    if (productPriceId) {
      path += `?productPriceId=${productPriceId}`
    }

    if (orderChargeId) {
      path += `${path.includes('?') ? '&' : '?'}orderChargeId=${orderChargeId}`
    }

    const { data } = await api.get(path)
    if (data) {
      if (data.customFields) {
        data.customFields = JSON.parse(data.customFields)
      }
      return data as TCheckoutOrderDetails
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getSubscriptionDetails = async ({
  codeId,
}: {
  codeId: string
}): Promise<TSubscriptionDetails | IErrorResponse> => {
  try {
    var path = `/Subscription/${codeId}`

    const { data } = await api.get<TSubscriptionDetails>(path)
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getCheckoutAccess = async ({
  page,
  rows,
  search,
  report,
  startDate,
  endDate,
  affiliateds,
  payments,
  products,
  sellerType,
  status,
}: IAccessPaginationProp): Promise<
  TCheckoutAccessPagination | IErrorResponse
> => {
  try {
    var path = `/Report/page/accesses?PageNumber=${page}&RowsPerPage=${rows}&Report=${report}`

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    if (startDate) {
      endDate ??= new Date()
      path += `&StartPeriod=${startDate.toISOString()}&EndPeriod=${endDate.toISOString()}`
    }

    if (
      affiliateds !== null &&
      affiliateds !== undefined &&
      affiliateds.length > 0
    ) {
      path += `&Affiliateds=${affiliateds.join(';')}`
    }

    if (payments !== null && payments !== undefined && payments.length > 0) {
      path += `&Payments=${payments.join(';')}`
    }

    if (products !== null && products !== undefined && products.length > 0) {
      path += `&Products=${products.join(';')}`
    }

    if (sellerType !== null && sellerType !== undefined) {
      path += `&SellerType=${sellerType}`
    }

    if (status !== null && status !== undefined && status.length > 0) {
      path += `&Status=${status.join(';')}`
    }

    const { data } = await api.get<TCheckoutAccessPagination>(path)
    //const { data } = checkoutOrders;
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getOrdersAnalitycs = async ({
  page,
  rows,
  search,
  report,
  startDate,
  endDate,
  affiliateds,
  payments,
  products,
  sellerType,
  status,
  currency,
}: IAccessPaginationProp): Promise<
  TCheckoutOrderPagination | IErrorResponse
> => {
  try {
    var path = `/Report/order/admin/accesses?PageNumber=${page}&RowsPerPage=${rows}&Report=${report}`

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    if (startDate) {
      endDate ??= new Date()
      path += `&StartPeriod=${startDate.toISOString()}&EndPeriod=${endDate.toISOString()}`
    }

    if (
      affiliateds !== null &&
      affiliateds !== undefined &&
      affiliateds.length > 0
    ) {
      path += `&Affiliateds=${affiliateds.join(';')}`
    }

    if (payments !== null && payments !== undefined && payments.length > 0) {
      path += `&Payments=${payments.join(';')}`
    }

    if (products !== null && products !== undefined && products.length > 0) {
      path += `&Products=${products.join(';')}`
    }

    if (sellerType !== null && sellerType !== undefined) {
      path += `&SellerType=${sellerType}`
    }

    if (status !== null && status !== undefined && status.length > 0) {
      path += `&Status=${status.join(';')}`
    }

    if (currency) path += `&Currency=${currency}`

    const { data } = await api.get<TCheckoutOrderPagination>(path)
    //const { data } = checkoutOrders;
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getAnticipations = async ({
  page,
  rows,
  search,
  filters,
}: ICheckoutOrderAnticipationsProp): Promise<
  TCheckoutOrderAnticipationPagination | IErrorResponse
> => {
  let { startDate, endDate, status } = filters

  try {
    var path = `/Report/anticipation?PageNumber=${page}&RowsPerPage=${rows}`

    if (search && search !== '') {
      path += `&Search=${search}`
    }

    if (startDate) {
      endDate ??= new Date()
      path += `&StartPeriod=${startDate.toISOString()}&EndPeriod=${endDate.toISOString()}`
    }

    if (status !== null && status !== undefined && status.length > 0) {
      path += `&Status=${status.join(';')}`
    }

    const { data } = await api.get<TCheckoutOrderAnticipationPagination>(path)
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getPaymentLink = async ({
  codeId,
  chargeId,
}: {
  codeId: string
  chargeId: string
}): Promise<IResponse> => {
  try {
    const { data: response } = await api.get(
      `/Order/${codeId}/PaymentLink/${chargeId}`
    )
    if (response) {
      return {
        success: true,
        data: response,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const updateTracking = async ({ data }: { data: any }): Promise<IResponse> => {
  try {
    const { data: response } = await api.put('/Order/tracking', data)
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

// SystemAdmin usa /unique com o id da cobrança; produtor usa o id do pedido
const deleteCharge = async ({
  orderChargeId,
  checkoutOrderId,
  isAdmin,
}: {
  orderChargeId: string
  checkoutOrderId: string
  isAdmin: boolean
}): Promise<IResponse> => {
  try {
    const { data: response } = await api.delete(
      isAdmin
        ? `/Order/charge/${orderChargeId}/unique`
        : `/Order/charge/${checkoutOrderId}`
    )
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const manualPayment = async ({
  checkoutOrderId,
}: {
  checkoutOrderId: string
}): Promise<IResponse> => {
  try {
    const { data: response } = await api.post(
      `/Order/${checkoutOrderId}/payment`
    )
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const subscriptionPaymentCancel = async ({
  checkoutOrderId,
}: {
  checkoutOrderId: string
}): Promise<IResponse> => {
  try {
    const { data: response } = await api.put(
      `Subscription/${checkoutOrderId}/cancel/payment`
    )
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const subscriptionManualCharge = async ({
  subscriptionCodeId,
}: {
  subscriptionCodeId: string
}): Promise<IResponse> => {
  try {
    const { data: response } = await api.post(
      `Subscription/manual/charge/${subscriptionCodeId}`
    )
    if (response) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const unlockSale = async ({
  checkoutOrderId,
}: {
  checkoutOrderId: string
}): Promise<IResponse> => {
  try {
    await api.post(`/Order/${checkoutOrderId}/unlock`)
    return {
      success: true,
      code: 0,
      error: '',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const reachedOutForAbandonedOrder = async ({
  orderId,
}: {
  orderId: string
}): Promise<IResponse> => {
  try {
    const { status } = await api.put(`/Report/${orderId}/reached`)
    if (status >= 200 && status <= 299) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const getCheckoutOrderEmails = async ({
  id,
}: {
  id: string
}): Promise<TCheckoutOrderEmail[] | IErrorResponse> => {
  try {
    var path = `/Order/${id}/emails`

    const { data } = await api.get<TCheckoutOrderEmail[]>(path)
    if (data) {
      return data
    }

    return {
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const resendEmail = async ({
  checkoutOrderId,
  email,
}: {
  checkoutOrderId: string
  email: string
}): Promise<IResponse> => {
  try {
    const { status } = await api.post(
      `/Order/${checkoutOrderId}/resend-email`,
      { email }
    )
    if (status >= 200 && status <= 299) {
      return {
        success: true,
        code: 0,
        error: '',
      }
    }

    return {
      success: false,
      code: 1,
      error: 'Erro inesperado do servidor',
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const OrderController = {
  create,
  verified,
  calculeIsntallments,
  getOpen,
  getAll,
  getApproved,
  getCheckoutOrderDetails,
  getSubscriptionDetails,
  updateTracking,
  deleteCharge,
  getCheckoutAccess,
  getOrdersAnalitycs,
  manualPayment,
  getAnticipations,
  subscriptionPaymentCancel,
  subscriptionManualCharge,
  unlockSale,
  getPaymentLink,
  reachedOutForAbandonedOrder,
  getCheckoutOrderEmails,
  resendEmail,
}

export default OrderController
